import * as React from 'react';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Paragraph from '@tiptap/extension-paragraph';
import Underline from '@tiptap/extension-underline';
import TiptapLink from '@tiptap/extension-link';
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Underline as UnderlineIcon,
  List as ListIcon,
  ListOrdered as ListOrderedIcon,
  Link2 as LinkIcon,
  RemoveFormatting as ClearIcon,
} from 'lucide-react';
import { Button } from './button';
import { Icon } from './Icon';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Input } from './input';
import { cn } from '../../lib/utils';
import { sanitizeRichText, isSafeUrl } from '../../lib/sanitizeHtml';
import './rich-text.css';

/**
 * RichText — the kit rich-text field, built on TipTap. Replaces the
 * hand-rolled `RichTextEditor` (`document.execCommand` + bare
 * contenteditable) the former Tasks page had (now the Tasks Kit's
 * TaskSheet). Same stored HTML format in and out — no migration needed for
 * existing rows — with the toolbar
 * pared to the exact button set the old editor had: Bold, Italic,
 * Underline, bullet list, numbered list, Link, Clear formatting.
 *
 * `richTextExtensions()` and `normalizeForCompare()` are exported
 * separately from the React component so a test can exercise the real
 * TipTap/ProseMirror schema and the sanitizer without mounting React (this
 * repo has no React test renderer — see `src/renderer/lib/usePref.test.ts`).
 */

/**
 * The exact extension set the editor runs, pulled out as a pure function so
 * tests can drive the real `@tiptap/core` `Editor` headlessly with the same
 * schema this component mounts.
 *
 * StarterKit nodes the old toolbar could never produce (headings,
 * blockquote, code/code block, strike, horizontal rule) are turned off —
 * not because leaving them on would corrupt existing data (`sanitizeHtml`'s
 * allow-list already flattens those tags to plain text before they ever
 * reach this schema, on load), but to keep live typing confined to the
 * toolbar's actual capabilities (StarterKit's markdown input rules would
 * otherwise let someone type "# " into a task description and get a
 * heading the toolbar never offered).
 *
 * `paragraph` is swapped for a copy that also parses `<div>` as a paragraph
 * boundary: a plain contenteditable's default Enter behaviour (what the old
 * editor relied on, never having set `defaultParagraphSeparator`) wraps each
 * line in a `<div>`, not a `<p>` — without this, those line breaks would
 * collapse into one run of text on load.
 */
export function richTextExtensions() {
  return [
    StarterKit.configure({
      paragraph: false,
      heading: false,
      blockquote: false,
      code: false,
      codeBlock: false,
      strike: false,
      horizontalRule: false,
    }),
    Paragraph.extend({
      parseHTML() {
        return [{ tag: 'p' }, { tag: 'div' }];
      },
    }),
    Underline,
    TiptapLink.configure({
      openOnClick: false,
      autolink: false,
      linkOnPaste: false,
      HTMLAttributes: { rel: 'noopener noreferrer' },
    }),
  ];
}

/**
 * Normalizes an HTML string for structural comparison in tests: collapses
 * insignificant whitespace between/around tags and re-serializes through a
 * detached element (which also normalizes attribute quoting/order). Not
 * used by the component itself — a test-support export for comparing "did
 * formatting/text survive" without caring about incidental whitespace.
 */
export function normalizeForCompare(html: string): string {
  const container = document.createElement('div');
  container.innerHTML = html ?? '';
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const toStrip: Text[] = [];
  let node: Node | null = walker.nextNode();
  while (node) {
    const text = node as Text;
    const collapsed = text.data.replace(/\s+/g, ' ');
    if (collapsed.trim() === '') {
      toStrip.push(text);
    } else {
      text.data = collapsed.trim();
    }
    node = walker.nextNode();
  }
  toStrip.forEach((t) => t.parentNode?.removeChild(t));
  return container.innerHTML;
}

function currentHtml(editor: Editor): string {
  return editor.isEmpty ? '' : editor.getHTML();
}

export interface RichTextProps {
  /** Stored HTML string — the same format the legacy editor read/wrote. */
  value: string;
  /** Fires with the sanitized HTML on a real user edit only — never on
   *  mount and never when `value` changes from the outside. */
  onChange: (html: string) => void;
  placeholder?: string;
  /** Greys the field out and blocks editing (e.g. while saving) but keeps
   *  the toolbar visible/inert. */
  disabled?: boolean;
  /** Renders the sanitized content with no toolbar and no editing — for
   *  people without edit rights on this field. */
  readOnly?: boolean;
  ariaLabel?: string;
  /** Minimum visible rows before the field scrolls. Default 4 (old editor's
   *  fixed 80px floor). */
  minRows?: number;
  className?: string;
}

function ToolbarButton({
  label,
  active,
  disabled,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={cn('h-8 w-8', active && 'bg-surface-alt text-primary')}
      aria-label={label}
      aria-pressed={!!active}
      title={label}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()} // keep selection/focus in the editor
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

function ToolbarDivider() {
  return <div className="mx-1 h-5 w-px bg-border" aria-hidden="true" />;
}

function LinkControl({ editor, disabled }: { editor: Editor; disabled?: boolean }) {
  const [open, setOpen] = React.useState(false);
  const [url, setUrl] = React.useState('');
  const isActive = editor.isActive('link');

  React.useEffect(() => {
    if (open) {
      setUrl((editor.getAttributes('link').href as string | undefined) ?? '');
    }
  }, [open, editor]);

  function apply() {
    const href = url.trim();
    if (!href) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      setOpen(false);
      return;
    }
    if (!isSafeUrl(href)) return; // reject silently — same scheme policy as sanitizeHtml
    editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    setOpen(false);
  }

  function remove() {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn('h-8 w-8', isActive && 'bg-surface-alt text-primary')}
          aria-label="Link"
          aria-pressed={isActive}
          title="Link"
          disabled={disabled}
        >
          <Icon icon={LinkIcon} size="sm" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-64 rounded-md border border-border bg-surface p-2 shadow-lg"
      >
        <div className="flex flex-col gap-2">
          <Input
            autoFocus
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://…"
            aria-label="Link URL"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                apply();
              } else if (e.key === 'Escape') {
                setOpen(false);
              }
            }}
          />
          <div className="flex justify-end gap-1">
            {isActive && (
              <Button type="button" variant="ghost" size="sm" onClick={remove}>
                Remove
              </Button>
            )}
            <Button type="button" variant="secondary" size="sm" onClick={apply}>
              {isActive ? 'Update' : 'Add'}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function RichText({
  value,
  onChange,
  placeholder,
  disabled,
  readOnly,
  ariaLabel,
  minRows = 4,
  className,
}: RichTextProps) {
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;
  const [isEmpty, setIsEmpty] = React.useState(true);
  const mountedRef = React.useRef(false);

  const editor = useEditor({
    extensions: richTextExtensions(),
    content: sanitizeRichText(value),
    editable: !disabled && !readOnly,
    editorProps: {
      attributes: {
        class: 'sf-rt-content',
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-label': ariaLabel || placeholder || 'Rich text editor',
      },
    },
    onCreate: ({ editor: ed }) => setIsEmpty(ed.isEmpty),
    onUpdate: ({ editor: ed, transaction }) => {
      setIsEmpty(ed.isEmpty);
      if (!transaction.docChanged) return;
      onChangeRef.current(sanitizeRichText(currentHtml(ed)));
    },
  });

  // Controlled-value sync: picks up an external change to `value` (e.g.
  // switching to a different record while this instance stays mounted)
  // without re-emitting onChange — `setContent(html, false)` suppresses
  // `onUpdate`. Skips the very first run since `content` above already set
  // the initial value.
  React.useEffect(() => {
    if (!editor) return;
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    const incoming = sanitizeRichText(value);
    if (incoming !== currentHtml(editor)) {
      editor.commands.setContent(incoming, false);
      setIsEmpty(editor.isEmpty);
    }
  }, [value, editor]);

  React.useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled && !readOnly);
  }, [editor, disabled, readOnly]);

  if (!editor) return null;

  return (
    <div
      className={cn(
        'sf-rt overflow-hidden rounded-md border border-border bg-surface',
        disabled && 'opacity-60',
        className
      )}
      data-disabled={!!disabled}
      data-readonly={!!readOnly}
    >
      {!readOnly && (
        <div className="flex flex-wrap items-center gap-1 border-b border-border bg-surface-alt px-2 py-1">
          <ToolbarButton
            label="Bold"
            active={editor.isActive('bold')}
            disabled={disabled}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <Icon icon={BoldIcon} size="sm" />
          </ToolbarButton>
          <ToolbarButton
            label="Italic"
            active={editor.isActive('italic')}
            disabled={disabled}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <Icon icon={ItalicIcon} size="sm" />
          </ToolbarButton>
          <ToolbarButton
            label="Underline"
            active={editor.isActive('underline')}
            disabled={disabled}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <Icon icon={UnderlineIcon} size="sm" />
          </ToolbarButton>
          <ToolbarDivider />
          <ToolbarButton
            label="Bullet list"
            active={editor.isActive('bulletList')}
            disabled={disabled}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <Icon icon={ListIcon} size="sm" />
          </ToolbarButton>
          <ToolbarButton
            label="Numbered list"
            active={editor.isActive('orderedList')}
            disabled={disabled}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <Icon icon={ListOrderedIcon} size="sm" />
          </ToolbarButton>
          <ToolbarDivider />
          <LinkControl editor={editor} disabled={disabled} />
          <ToolbarButton
            label="Clear formatting"
            disabled={disabled}
            onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
          >
            <Icon icon={ClearIcon} size="sm" />
          </ToolbarButton>
        </div>
      )}
      <div className="relative" style={{ '--rt-min-rows': minRows } as React.CSSProperties}>
        {isEmpty && !readOnly && (
          <div className="sf-rt-placeholder pointer-events-none absolute" aria-hidden="true">
            {placeholder || 'Type here...'}
          </div>
        )}
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
